import {
	ActionMenu,
	ActionMenuItem,
	ActionMenuTrigger,
	MoreButton,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { OrdersTableRow } from '@ekhodealer/ekho-common';

import Box24 from '@common/components/icons/Box24';
import ClockFading24 from '@common/components/icons/ClockFading24';
import DocumentText24 from '@common/components/icons/DocumentText24';
import Info24 from '@common/components/icons/Info24';
import SquareUp24 from '@common/components/icons/SquareUp24';
import WalletCard24 from '@common/components/icons/WalletCard24';

import { useExportOrders } from '../api/useExportOrders';
import { OrderDetailsTab } from '../atoms/orderDetailsDrawerAtoms';
import { useOrderNavigation } from '../hooks/useOrderNavigation';

export interface OrderActionMenuProps {
	item: OrdersTableRow;
}

export const OrderActionMenu = ({ item }: OrderActionMenuProps) => {
	const { openOrderDetails } = useOrderNavigation();
	const exportOrdersMutation = useExportOrders();

	// Action handlers - all defined within the component for self-containment
	const handleViewOrderProgress = () => {
		openOrderDetails(item.orderId, OrderDetailsTab.PROGRESS);
	};

	const handleViewOrderDetails = () => {
		openOrderDetails(item.orderId, OrderDetailsTab.DETAILS);
	};

	const handleViewPaymentAndFai = () => {
		openOrderDetails(item.orderId, OrderDetailsTab.PAYMENT_AND_FAI);
	};

	const handleViewDocuments = () => {
		openOrderDetails(item.orderId, OrderDetailsTab.DOCUMENTS);
	};

	const handleViewOrderTimeline = () => {
		openOrderDetails(item.orderId, OrderDetailsTab.TIMELINE);
	};

	const handleExportAsCsv = async () => {
		try {
			await exportOrdersMutation.mutateAsync({
				purchasedVehicleIds: [item.purchasedVehicleId],
			});

			toast({
				message: 'Order exported',
				variant: 'success',
			});
		} catch {
			toast({
				message: 'Failed to export order. Please try again.',
				variant: 'error',
			});
		}
	};

	return (
		<ActionMenuTrigger>
			<MoreButton size='medium' aria-label={`More actions for order ${item.orderId}`} />
			<ActionMenu>
				<ActionMenuItem
					label='View order progress'
					leadingAccessory={<Box24 />}
					onAction={handleViewOrderProgress}
				/>
				<ActionMenuItem
					label='View order details'
					leadingAccessory={<Info24 />}
					onAction={handleViewOrderDetails}
				/>
				<ActionMenuItem
					label='View payment & F&I'
					leadingAccessory={<WalletCard24 />}
					onAction={handleViewPaymentAndFai}
				/>
				<ActionMenuItem
					label='View documents'
					leadingAccessory={<DocumentText24 />}
					onAction={handleViewDocuments}
				/>
				<ActionMenuItem
					label='View timeline'
					leadingAccessory={<ClockFading24 />}
					onAction={handleViewOrderTimeline}
				/>
				<ActionMenuItem
					label='Export as CSV'
					leadingAccessory={<SquareUp24 />}
					onAction={handleExportAsCsv}
				/>
			</ActionMenu>
		</ActionMenuTrigger>
	);
};
